import {
  CarOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  ShoppingCartOutlined,
  StarOutlined
} from '@ant-design/icons';
import { Button, Col, Form, Input, message, Modal, Row, Select, Spin } from 'antd';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginModal from '../components/LoginModal';
import { useCart } from '../contexts/CartContext';
import { useProducts } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { useIsMobile } from '../hooks/useIsMobile';
import { useI18n } from '../i18n';
import { apiService, MediaGalleryItem, Product } from '../services/api';
import '../styles/common.css';
import '../styles/HomePage.css';

const { TextArea } = Input;
const { Option } = Select;

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [productModalVisible, setProductModalVisible] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [loginModalVisible, setLoginModalVisible] = useState(false);

  // 使用自定义 Hook 检测是否为移动端
  const isMobile = useIsMobile();

  // Media preview related state
  const [mediaPreviewVisible, setMediaPreviewVisible] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaGalleryItem | null>(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [currentProductMedia, setCurrentProductMedia] = useState<MediaGalleryItem[]>([]);
  const [form] = Form.useForm();
  const { t } = useI18n();
  const { isAuthenticated, user } = useAuth();

  // Use SWR to fetch Taiwan products data
  const { products: taiwanProducts, isLoading: productsLoading, error: productsError } = useProducts(0, 4);

  // Handle product loading errors
  useEffect(() => {
    if (productsError) {
      console.error('Failed to fetch Taiwan products:', productsError);
      message.error(t('messages.products.loadFailed'));
    }
  }, [productsError]);

  // Listen for contact form events in carousel
  useEffect(() => {
    const handleOpenContactModal = () => {
      setContactModalVisible(true);
    };

    window.addEventListener('openContactModal', handleOpenContactModal);
    return () => {
      window.removeEventListener('openContactModal', handleOpenContactModal);
    };
  }, []);

  // Handle media click
  const handleMediaClick = (media: MediaGalleryItem, product: Product) => {
    if (product.mediaGalleryList && product.mediaGalleryList.length > 0) {
      const mediaIndex = product.mediaGalleryList.findIndex(m => m.id === media.id);
      setCurrentMediaIndex(mediaIndex);
      setCurrentProductMedia(product.mediaGalleryList);
      setSelectedMedia(media);
      setMediaPreviewVisible(true);
    }
  };

  // Handle media preview navigation
  const handleMediaPrev = () => {
    if (currentMediaIndex > 0) {
      const newIndex = currentMediaIndex - 1;
      setCurrentMediaIndex(newIndex);
      setSelectedMedia(currentProductMedia[newIndex]);
    }
  };

  const handleMediaNext = () => {
    if (currentMediaIndex < currentProductMedia.length - 1) {
      const newIndex = currentMediaIndex + 1;
      setCurrentMediaIndex(newIndex);
      setSelectedMedia(currentProductMedia[newIndex]);
    }
  };

  // Add necessary styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      body {
        margin: 0;
        padding: 0;
      }
      
      /* Modern product card styles */
      .product-card-modern {
        background: white;
        overflow: hidden;
        transition: all 0.4s ease;
        height: 100%;
        position: relative;
        border: none !important;
        padding: 0 !important;
        min-height: 380px;
        max-width: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
      }
      
      .product-card-modern .ant-card-body {
        padding: 0 !important;
      }
      
      .product-card-modern:hover {
        transform: translateY(-8px);
        box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
      }
      
      .product-image-container {
        position: relative;
        height: 220px;
        overflow: hidden;
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        width: 100%;
      }
      
      .product-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.4s ease;
      }
      
      .product-card-modern:hover .product-image {
        transform: scale(1.05);
      }
      
      .product-info {
        padding: 1.2rem;
        position: relative;
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
      
      .product-name {
        font-size: 1.1rem;
        font-weight: 600;
        color: #333;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      
      .product-price {
        display: flex;
        align-items: baseline;
      }
      
      .price-symbol {
        font-size: 0.9rem;
        color: #666;
        margin-right: 2px;
      }
      
      .price-amount {
        font-size: 1.5rem;
        font-weight: 700;
        color: #1890ff;
        line-height: 1;
      }
      
      .product-badge {
        position: absolute;
        top: 1rem;
        right: 1rem;
        padding: 0.25rem 0.75rem;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 600;
        color: white;
        background: '#ffffff';
      }
      
      .product-badge.new {
        background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
      }
      
      .product-badge.essential {
        background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
      }
      
      .product-badge.accessory {
        background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
      }
      
      .service-promise {
        text-align: center;
        padding: 1.5rem 1rem;
        background: white;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        border: 1px solid #f0f0f0;
        transition: all 0.3s ease;
        height: 100%;
      }
      
      .service-promise:hover {
        transform: translateY(-3px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
      }
      
      .service-promise .anticon {
        font-size: 3rem;
      }
      
      .service-promise h5 {
        font-size: 1.2rem;
        font-weight: 600;
        margin-top: 0.75rem;
        margin-bottom: 0.5rem;
      }
      
      .service-promise p {
        font-size: 1rem;
        line-height: 1.5;
        margin-bottom: 0;
      }
      
      .feature-card-modern {
        background: white;
        border-radius: 12px;
        padding: 1.5rem;
        text-align: center;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        border: 1px solid #f0f0f0;
        transition: all 0.3s ease;
        height: 100%;
      }
      
      .feature-card-modern:hover {
        transform: translateY(-5px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }
      
      .feature-icon-wrapper {
        width: 64px;
        height: 64px;
        background: #17a2b8;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1rem;
        color: white;
        box-shadow: 0 4px 12px rgba(23, 162, 184, 0.3);
      }
      
      .feature-icon-wrapper .anticon {
        font-size: 2rem !important;
      }
      
      .feature-title {
        color: #333;
        font-size: 1.1rem;
        font-weight: 600;
        margin-bottom: 0.75rem;
      }
      
      .feature-description {
        color: #666;
        line-height: 1.5;
        margin-bottom: 0.75rem;
        font-size: 0.95rem;
      }
      
      .feature-badge {
        display: inline-block;
        background: rgba(255, 215, 0, 0.1);
        color: #ff8c00;
        padding: 0.25rem 0.6rem;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 600;
        border: 1px solid rgba(255, 215, 0, 0.3);
      }
      
      /* Mobile adjustments */
      @media (max-width: 768px) {
        .service-promise {
          padding: 1.2rem 0.8rem;
        }
        
        .service-promise .anticon {
          font-size: 2.5rem;
        }
        
        .service-promise h5 {
          font-size: 1.1rem;
          margin-top: 0.5rem;
        }
        
        .service-promise p {
          font-size: 0.9rem;
        }
        
        .product-image-container {
          height: 180px;
        }
        
        .product-info {
        }
        
        .product-name {
          font-size: 1rem;
        }
        
        .price-amount {
          font-size: 1.3rem;
        }
        
        .product-badge {
          top: 0.75rem;
          right: 0.75rem;
          padding: 0.2rem 0.6rem;
          font-size: 0.7rem;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleContactSubmit = async (values: any) => {
    console.log('Contact form values:', values);
    try {
      if (isAuthenticated && user) {
        // User is logged in, call customer service chat API
        // First build message content, including all form information
        const messageContent = `
${t('messages.contact.name')}: ${values.name}
${t('messages.contact.email')}: ${values.email}
${t('messages.contact.phone')}: ${values.phone || t('messages.contact.notProvided')}
${t('messages.contact.subject')}: ${values.subject || t('messages.contact.contactConsultation')}
${t('messages.contact.message')}: ${values.message}
        `.trim();

        // Call customer service chat send API
        const response = await apiService.sendChatMessage(messageContent);

        if (response.success) {
          message.success(t('forms.homeContact.success'));
          // Can navigate to customer service page
          setContactModalVisible(false);
          form.resetFields();
        } else {
          message.error(response.message || t('messages.sendFailed'));
        }
      } else {
        // User not logged in, call stranger message API
        const response = await apiService.sendStrangerMessage({
          name: values.name,
          email: values.email,
          phone: values.phone,
          subject: values.subject || t('forms.contact.title'),
          message: values.message
        });

        if (response.success) {
          message.success(t('forms.homeContact.success'));
          setContactModalVisible(false);
          form.resetFields();
        } else {
          message.error(response.message || t('messages.sendFailed'));
        }
      }
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      message.error(t('messages.sendFailed'));
    }
  };

  const handleProductClick = () => {
    setProductModalVisible(true);
  };

  const handleAddToCart = (product: any) => {
    // 检查用户是否登录
    if (!isAuthenticated) {
      setLoginModalVisible(true);
      return;
    }

    // Add product to cart
    const productData: Product = {
      id: product.id,
      name: product.name,
      price: product.price,
      imageUrl: product.imageUrl || '/img/placeholder.png',
      description: product.description || '',
      stock: product.stock || 999,
      category: product.category || 'default',
      active: product.active !== undefined ? product.active : true,
      sku: product.sku || `SKU-${product.id}`,
      createdAt: product.createdAt || new Date().toISOString(),
      updatedAt: product.updatedAt || new Date().toISOString(),
      mediaGalleryList: product.mediaGalleryList || []
    };

    addItem(productData, 1);

    // Show success message
    message.success(t('cart.addToCartSuccess'));
  };

  const handleBuyNowClick = (product: any) => {
    // 检查用户是否登录
    if (!isAuthenticated) {
      setLoginModalVisible(true);
      return;
    }

    // Add to cart first
    handleAddToCart(product);

    // Then open cart - notify Cart component in Navbar through custom event
    window.dispatchEvent(new CustomEvent('openCart'));
  };

  // 登录成功后的回调
  const handleLoginSuccess = () => {
    setLoginModalVisible(false);
    message.success(t('messages.auth.loginSuccess'));
  };

  return (
    <div className="home-page">
      {/* Hero Carousel */}
      <div style={{ width: '100%', lineHeight: 0, margin: 0 }}>
        <img src={'/img/phone/19.jpg'} style={{ width: '100%', display: 'block', margin: 0 }} alt="spacing" />
      </div>
      {/* Taiwan Products */}
      <div id="taiwan-products" style={{ background: '#9cd5e0', paddingTop: '40px', paddingBottom: '80px', margin: 0 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
          <h2 className="h1-responsive h1 text-center" style={{ marginTop: '20px', marginBottom: '40px' }}>{t('products.title')}</h2>

          <Spin spinning={productsLoading}>
            <Row gutter={[32, 32]} className="">
              {taiwanProducts.length > 0 ? (
                taiwanProducts.map((product, index) => (
                  <Col lg={12} md={12} xs={12} key={product.id}>
                    <div
                      className="product-card-modern"

                      onClick={() => navigate(`/product/${product.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="product-image-container">
                        {product.mediaGalleryList && product.mediaGalleryList.length > 0 ? (
                          <img
                            src={product.mediaGalleryList[0].ossPath || '/img/placeholder.png'}
                            alt={product.name}
                            className="product-image"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMediaClick(product.mediaGalleryList![0], product);
                            }}
                            style={{ cursor: 'pointer' }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/img/placeholder.png';
                            }}
                          />
                        ) : (
                          <img
                            src={product.imageUrl || '/img/logo.jpg'}
                            alt={product.name}
                            className="product-image"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = '/img/logo.jpg';
                            }}
                          />
                        )}
                      </div>
                      <div className="product-info">
                        <h5 className="product-name">{product.name}</h5>
                        <div className="product-price">
                          <span className="price-symbol">NT$</span>
                          <span className="price-amount">{((product.price || product.salePrice || product.basePrice || 0)).toFixed(0)}</span>
                        </div>
                        <div className="btn-group-unified" style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <Button
                            className="btn-outline-unified btn-sm-unified"
                            icon={<ShoppingCartOutlined />}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent event bubbling
                              handleAddToCart({
                                name: product.name,
                                price: product.price || product.salePrice || product.basePrice || 0,
                                id: product.id,
                                description: product.description,
                                stock: product.stock
                              });
                            }}
                          >
                            {t('productDetail.info.addToCart')}
                          </Button>
                          <Button
                            className="btn-primary-unified btn-sm-unified"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent event bubbling
                              handleBuyNowClick({
                                name: product.name,
                                price: product.price || product.salePrice || product.basePrice || 0,
                                id: product.id,
                                description: product.description,
                                stock: product.stock
                              });
                            }}
                          >
                            {t('home.sections.products.buttons.buyNow')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Col>
                ))
              ) : !productsLoading ? (
                <Col span={24} style={{ textAlign: 'center', padding: '50px' }}>
                  <p>{t('products.noProducts')}</p>
                </Col>
              ) : null}
            </Row>
          </Spin>
        </div>
      </div >

      {/* Service Promise */}
      <section className="py-5" style={{ background: '#ffffff', padding: '40px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
          <div className="text-center" style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '12px' }}>{t('home.sections.services.title')}</h2>
            <p style={{ color: '#666', fontSize: '1rem', margin: '0 auto', maxWidth: '600px' }}>
              {t('home.sections.services.subtitle')}
            </p>
          </div>
          <Row gutter={[16, 16]} className="text-center">
            <Col md={8} span={24}>
              <div className="service-promise">
                <CarOutlined />
                <h5>{t('home.sections.services.items.shipping.title')}</h5>
                <p>{t('home.sections.services.items.shipping.description')}</p>
              </div>
            </Col>
            <Col md={8} span={24}>
              <div className="service-promise">
                <ShoppingCartOutlined />
                <h5>{t('home.sections.services.items.freeShipping.title')}</h5>
                <p>{t('home.sections.services.items.freeShipping.description')}</p>
              </div>
            </Col>
            <Col md={8} span={24}>
              <div className="service-promise">
                <SafetyCertificateOutlined />
                <h5>{t('home.sections.services.items.quality.title')}</h5>
                <p>{t('home.sections.services.items.quality.description')}</p>
              </div>
            </Col>
          </Row>
        </div>
      </section >

      {/* Homepage Summary */}
      <section className="why-choose-section py-5" style={{ background: '#f8f9fa', padding: '40px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
          <div className="text-center" style={{ marginBottom: '32px' }}>
            <h2 className="text-info" style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '12px' }}>{t('home.sections.whyChoose.title')}</h2>
            <p className="lead" style={{ color: '#666', maxWidth: '600px', margin: '0 auto', fontSize: '1rem' }}>{t('home.sections.whyChoose.subtitle')}</p>
          </div>

          <div className="features-showcase">
            <Row gutter={[16, 16]}>
              <Col lg={8} md={12} span={24}>
                <div className="feature-card-modern">
                  <div className="feature-icon-wrapper">
                    <SafetyOutlined />
                  </div>
                  <div className="feature-content">
                    <h4 className="feature-title">{t('home.sections.whyChoose.features.0.title')}</h4>
                    <p className="feature-description">{t('home.sections.whyChoose.features.0.desc')}</p>
                    <div className="feature-badge">{t('home.sections.whyChoose.features.0.badge')}</div>
                  </div>
                </div>
              </Col>

              <Col lg={8} md={12} span={24}>
                <div className="feature-card-modern">
                  <div className="feature-icon-wrapper">
                    <SafetyOutlined />
                  </div>
                  <div className="feature-content">
                    <h4 className="feature-title">{t('home.sections.whyChoose.features.1.title')}</h4>
                    <p className="feature-description">{t('home.sections.whyChoose.features.1.desc')}</p>
                    <div className="feature-badge">{t('home.sections.whyChoose.features.1.badge')}</div>
                  </div>
                </div>
              </Col>

              <Col lg={8} md={12} span={24}>
                <div className="feature-card-modern">
                  <div className="feature-icon-wrapper">
                    <StarOutlined />
                  </div>
                  <div className="feature-content">
                    <h4 className="feature-title">{t('home.sections.whyChoose.features.2.title')}</h4>
                    <p className="feature-description">{t('home.sections.whyChoose.features.2.desc')}</p>
                    <div className="feature-badge">{t('home.sections.whyChoose.features.2.badge')}</div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

        </div>
      </section >
      <div style={{ textAlign: 'center', padding: '24px 0' }}>
        <Button
          type="primary"
          size="large"
          onClick={() => {
            const element = document.getElementById('taiwan-products');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
          style={{
            borderRadius: 0,
            padding: '10px 40px',
            fontSize: '16px',
            height: 'auto'
          }}
        >
          {t('home.sections.whyChoose.buttons.buyNow')}
        </Button>
      </div>
      {/* Contact Form Modal */}
      < Modal
        title={t('forms.homeContact.title')}
        open={contactModalVisible}
        onCancel={() => setContactModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form form={form} onFinish={handleContactSubmit} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label={t('forms.homeContact.name.label')} rules={[{ required: true, message: t('forms.homeContact.name.placeholder') }]}>
                <Input size="large" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label={t('forms.homeContact.phone.label')} rules={[{ required: true, message: t('forms.homeContact.phone.placeholder') }]}>
                <Input size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="email" label={t('forms.homeContact.email.label')}>
            <Input size="large" placeholder={t('forms.homeContact.email.placeholder')} />
          </Form.Item>
          <Form.Item name="company" label={t('forms.homeContact.company.label')}>
            <Input size="large" placeholder={t('forms.homeContact.company.placeholder')} />
          </Form.Item>
          <Form.Item name="message" label={t('forms.homeContact.message.label')} rules={[{ required: true, message: t('forms.homeContact.message.placeholder') }]}>
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item name="cooperation" label={t('forms.homeContact.cooperation.label')}>
            <Select placeholder={t('forms.homeContact.cooperation.placeholder')}>
              <Option value="retail">{t('forms.homeContact.cooperation.retail')}</Option>
              <Option value="wholesale">{t('forms.homeContact.cooperation.wholesale')}</Option>
              <Option value="odm">{t('forms.homeContact.cooperation.odm')}</Option>
              <Option value="oem">{t('forms.homeContact.cooperation.oem')}</Option>
            </Select>
          </Form.Item>
          <div className="text-center">
            <Button className="btn-primary-unified btn-lg-unified" htmlType="submit">
              {t('forms.homeContact.submit')}
            </Button>
          </div>
        </Form>
      </Modal >

      {/* Product Details Modal */}
      < Modal
        title={t('home.productModal.title')}
        open={productModalVisible}
        onCancel={() => setProductModalVisible(false)}
        footer={null}
        width={1000}
      >
        <Row gutter={24}>
          <Col span={12}>
            <div className="product-gallery">
              <div className="main-image">
                <img src="/img/IMG_1983.jpg" alt={t('home.productModal.product.title')} />
              </div>
              <div className="thumbnail-images mt-3">
                <img src="/img/IMG_2025.JPG" alt={t('home.productModal.gallery.alt1')} className="img-thumbnail" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                <img src="/img/IMG_1988.JPG" alt={t('home.productModal.gallery.alt2')} className="img-thumbnail" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
                <img src="/img/IMG_2011.JPG" alt={t('home.productModal.gallery.alt3')} className="img-thumbnail" style={{ width: '80px', height: '80px', objectFit: 'cover' }} />
              </div>
            </div>
          </Col>
          <Col span={12}>
            <div className="product-info">
              <h4>{t('home.productModal.product.title')}</h4>
              <p className="text-muted">{t('home.productModal.product.description')}</p>

              <div className="product-features">
                <h6>{t('home.productModal.product.features.title')}</h6>
                <ul className="list-unstyled">
                  <li><CheckCircleOutlined className="text-success mr-2" />{t('home.productModal.product.features.technology')}</li>
                  <li><CheckCircleOutlined className="text-success mr-2" />{t('home.productModal.product.features.comfort')}</li>
                  <li><CheckCircleOutlined className="text-success mr-2" />{t('home.productModal.product.features.hygiene')}</li>
                  <li><CheckCircleOutlined className="text-success mr-2" />{t('home.productModal.product.features.value')}</li>
                </ul>
              </div>

              <div className="product-specs mt-4">
                <h6>{t('home.productModal.product.specs.title')}</h6>
                <Row>
                  <Col span={12}>
                    <small className="text-muted">{t('home.productModal.product.specs.material.label')}</small><br />
                    <span>{t('home.productModal.product.specs.material.value')}</span>
                  </Col>
                  <Col span={12}>
                    <small className="text-muted">{t('home.productModal.product.specs.target.label')}</small><br />
                    <span>{t('home.productModal.product.specs.target.value')}</span>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>
        <div className="btn-group-unified" style={{ justifyContent: 'center', marginTop: '24px' }}>
          <Button
            className="btn-outline-unified"
            onClick={() => setProductModalVisible(false)}
          >
            {t('home.productModal.close')}
          </Button>
          <Button
            className="btn-primary-unified"
            onClick={() => {
              setProductModalVisible(false);
              setContactModalVisible(true);
            }}
          >
            {t('forms.homeContact.submit')}
          </Button>
        </div>
      </Modal >




      {/* Login Modal */}
      <LoginModal
        visible={loginModalVisible}
        onCancel={() => setLoginModalVisible(false)}
        onSuccess={handleLoginSuccess}
      />

    </div >
  );
};

export default HomePage;
